import { Injectable, inject, signal, computed } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { AuthService } from './auth.service';
import { NotificationsApiService, Notification } from '../../features/notifications/services/notifications-api.service';
import { NotificationSoundService } from './notification-sound.service';
import { RealtimeService } from './realtime.service';

@Injectable({ providedIn: 'root' })
export class NotificationsRealtimeService {
  private readonly auth = inject(AuthService);
  private readonly api = inject(NotificationsApiService);
  private readonly soundService = inject(NotificationSoundService);
  private readonly realtimeService = inject(RealtimeService);

  private readonly _connected = signal<boolean>(false);
  private readonly _unreadCount = signal<number>(0);
  private readonly incoming$ = new Subject<Notification>();
  private subscriptions: any[] = [];

  readonly connected = computed(() => this._connected());
  readonly unreadCount = computed(() => this._unreadCount());

  /**
   * Observable stream of incoming notifications
   */
  get notifications$(): Observable<Notification> {
    return this.incoming$.asObservable();
  }

  /**
   * Connect to the notifications WebSocket via unified gateway.
   * Safe to call multiple times; it will only connect once.
   */
  connect(): void {
    console.log('🔗 [DEBUG] NotificationsRealtimeService.connect() called');
    
    const user = this.auth.user();
    console.log('👤 [DEBUG] Current user:', user);
    
    if (!user) {
      console.log('❌ [DEBUG] No user found, cannot connect');
      return;
    }

    if (this._connected()) {
      console.log('✅ [DEBUG] Already connected, skipping');
      return;
    }

    console.log('🌐 [DEBUG] Connecting to unified gateway...');
    // Ensure unified gateway is connected
    this.realtimeService.connect();

    console.log('👂 [DEBUG] Setting up notification event listeners...');
    // Subscribe to notification events from unified gateway
    const sub1 = this.realtimeService.on('notifications:notification').subscribe((payload: Notification) => {
      console.log('🔔 [DEBUG] Notification received via unified gateway:', payload);
      this.incoming$.next(payload);
      this._unreadCount.update((current) => current + (payload.read ? 0 : 1));
      
      // Play notification sound for unread notifications
      if (!payload.read) {
        this.soundService.play().catch(err => {
          console.warn('Failed to play notification sound:', err);
        });
      }
    });

    const sub2 = this.realtimeService.on('notifications:unread_count').subscribe((data: { count: number }) => {
      console.log('📊 [DEBUG] Unread count received:', data);
      if (typeof data?.count === 'number') {
        this._unreadCount.set(data.count);
      }
    });

    // Also listen to general realtime events for notifications
    const sub3 = this.realtimeService.events.subscribe((event) => {
      console.log('📡 [DEBUG] General realtime event received:', event);
      if (event.service === 'notifications-service') {
        if (event.type === 'notifications:notification') {
          const payload = event.payload as Notification;
          console.log('🔔 [DEBUG] Notification from general events:', payload);
          this.incoming$.next(payload);
          if (!payload.read) {
            this._unreadCount.update((current) => current + 1);
            this.soundService.play().catch(() => {});
          }
        } else if (event.type === 'notifications:unread_count') {
          const data = event.payload as { count: number };
          console.log('📊 [DEBUG] Unread count from general events:', data);
          if (typeof data?.count === 'number') {
            this._unreadCount.set(data.count);
          }
        }
      }
    });

    // Monitor connection status
    const sub4 = this.realtimeService.connected.subscribe(connected => {
      console.log('🔌 [DEBUG] Realtime service connection status:', connected);
      this._connected.set(connected);
      if (connected) {
        console.log('✅ [DEBUG] Notifications WebSocket connected via unified gateway');
        // Load initial unread count from REST API
        this.refreshUnreadCount();
      } else {
        console.warn('❌ [DEBUG] Notifications WebSocket disconnected');
      }
    });

    this.subscriptions = [sub1, sub2, sub3, sub4];
    this._connected.set(this.realtimeService.isConnected());
    console.log('✅ [DEBUG] NotificationsRealtimeService setup complete');
  }
  



  /**
   * Disconnect explicitly (e.g. on logout)
   */
  disconnect(): void {
    // Unsubscribe from all events
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
    this._connected.set(false);
    // Don't reset unread count on disconnect - keep it for when user reconnects
  }

  /**
   * Refresh unread count via REST fallback.
   */
  refreshUnreadCount(): void {
    this.api
      .getNotifications({ read: false })
      .subscribe({
        next: (res) => {
          if (res.success) {
            this._unreadCount.set(res.data?.length ?? 0);
          }
        },
        error: (err) => {
          console.warn('Failed to refresh unread notifications count:', err);
        },
      });
  }
}


